import type { JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Copy, Plus } from 'lucide-react';
import { Button } from '@caratuva/ui';
import { getKybSubmission, getPayoutConfig, listInvoices } from '../lib/api';

/*
 * Invoice list. Plain HTML <table> for now — Agent Z's `Table` primitive
 * replaces this in a follow-up. The server-side `GET /v1/invoices` route
 * is contract-only at the moment; once the backend ships it, this view
 * lights up automatically.
 */

export const Route = createFileRoute('/_app/invoices/')({
  component: InvoicesIndex,
});

function InvoicesIndex(): JSX.Element {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['invoices'],
    queryFn: ({ signal }) => listInvoices(signal),
  });

  // Invoice creation requires an approved KYB AND a registered PIX payout key
  // (see assertSellerCanCollect on the API). Reuse the exact query keys the
  // Payout page uses so the cache is shared. Eligibility is only "known" once
  // both queries resolve; until then we render the CTA optimistically (the
  // /invoices/new route guard is the real protection) to avoid a flicker.
  const kybQuery = useQuery({
    queryKey: ['kyb-submission'],
    queryFn: ({ signal }) => getKybSubmission(signal),
  });
  const payoutQuery = useQuery({
    queryKey: ['payout-config'],
    queryFn: ({ signal }) => getPayoutConfig(signal),
  });
  const onboardingResolved = !kybQuery.isPending && !payoutQuery.isPending;
  const canCreateInvoice =
    kybQuery.data?.status === 'approved' && payoutQuery.data?.hasBankAccount === true;
  const blockCreate = onboardingResolved && !canCreateInvoice;
  const newInvoiceHint = intl.formatMessage({
    id: 'invoices.gate.newDisabledHint',
    defaultMessage: 'Complete KYB and PIX payout setup first',
  });

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'invoices.title', defaultMessage: 'Invoices' })}
        </h1>
        {blockCreate ? (
          <Button disabled title={newInvoiceHint}>
            <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            {intl.formatMessage({ id: 'invoices.new', defaultMessage: 'New invoice' })}
          </Button>
        ) : (
          <Button asChild>
            <Link to="/invoices/new">
              <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
              {intl.formatMessage({ id: 'invoices.new', defaultMessage: 'New invoice' })}
            </Link>
          </Button>
        )}
      </div>

      {blockCreate ? (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">
            {intl.formatMessage({
              id: 'invoices.gate.banner',
              defaultMessage:
                'Finish onboarding to create invoices: complete business verification (KYB) and register a PIX payout key.',
            })}
          </p>
          <Link
            to="/onboarding"
            className="mt-1 inline-block text-sm font-medium text-amber-800 underline"
          >
            {intl.formatMessage({
              id: 'invoices.gate.cta',
              defaultMessage: 'Go to Onboarding',
            })}
          </Link>
        </div>
      ) : null}

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'invoices.loading', defaultMessage: 'Loading invoices...' })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'invoices.error',
            defaultMessage: 'Failed to load invoices.',
          })}
        </p>
      ) : query.data.length === 0 ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'invoices.empty', defaultMessage: 'No invoices yet.' })}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-navy-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-navy-200 bg-navy-50 text-xs uppercase tracking-wide text-navy-700">
              <tr>
                <th className="px-4 py-2">
                  {intl.formatMessage({ id: 'invoices.column.id', defaultMessage: 'ID' })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({
                    id: 'invoices.column.buyerEmail',
                    defaultMessage: 'Buyer',
                  })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({ id: 'invoices.column.seller', defaultMessage: 'Seller' })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({ id: 'invoices.column.amount', defaultMessage: 'Amount' })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({ id: 'invoices.column.status', defaultMessage: 'Status' })}
                </th>
                <th className="px-4 py-2">
                  {intl.formatMessage({
                    id: 'invoices.column.createdAt',
                    defaultMessage: 'Created',
                  })}
                </th>
                <th className="px-4 py-2 text-right">
                  <span className="sr-only">
                    {intl.formatMessage({
                      id: 'invoices.column.actions',
                      defaultMessage: 'Actions',
                    })}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((invoice) => (
                <tr key={invoice.id} className="border-b border-navy-100 last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link
                      to="/invoices/$id"
                      params={{ id: invoice.id }}
                      className="text-navy-700 hover:underline"
                    >
                      {invoice.publicId}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    {invoice.buyerName ? (
                      <span className="text-navy-900">{invoice.buyerName}</span>
                    ) : invoice.buyerEmail ? (
                      <span className="text-navy-700">{invoice.buyerEmail}</span>
                    ) : (
                      <span className="font-mono text-xs text-navy-700">{invoice.buyerId}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {invoice.sellerName ? (
                      <span className="text-navy-900">{invoice.sellerName}</span>
                    ) : (
                      <span className="text-navy-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 tabular-nums">
                    {invoice.amount} {invoice.currency}
                  </td>
                  <td className="px-4 py-2">{invoice.status}</td>
                  <td className="px-4 py-2 tabular-nums">
                    {new Date(invoice.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {blockCreate ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled
                        title={newInvoiceHint}
                        aria-label={intl.formatMessage(
                          {
                            id: 'invoices.row.duplicateAria',
                            defaultMessage: 'Duplicate invoice {publicId}',
                          },
                          { publicId: invoice.publicId },
                        )}
                      >
                        <Copy aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    ) : (
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        title={intl.formatMessage({
                          id: 'invoices.row.duplicate',
                          defaultMessage: 'Duplicate invoice',
                        })}
                      >
                        <Link
                          to="/invoices/new"
                          search={{ from: invoice.id }}
                          aria-label={intl.formatMessage(
                            {
                              id: 'invoices.row.duplicateAria',
                              defaultMessage: 'Duplicate invoice {publicId}',
                            },
                            { publicId: invoice.publicId },
                          )}
                        >
                          <Copy aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                        </Link>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
